
import { useEffect, useRef } from 'react';
import type { FC } from 'react';
import { useThreadStore } from '@/store/thread.store';
import { useRealtime, BroadcastEventType, BroadcastMessage } from '@/lib/realtime/core/useRealtime';
import { SocketEvent, ThreadComment, TaskWithAssignments } from '@scrambl/shared';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/hooks/query-keys';
import { createLogger } from '@/lib/logger';

const logger = createLogger('components/shared/RealTimeSync');

declare global {
  interface Window {
    __realtimeSyncInstances?: number;
__realtimeEventCounter?: Map<string, number>;
    __realtimeEventTracker?: Map<string, number>;
  }
}

interface ThreadEventData {
  id: string;
  [key: string]: unknown;
}

interface ThreadCommentEventData {
  id: string;
  threadId: string;
  [key: string]: unknown;
}

interface TaskDeletedEventData {
  id?: string;
  taskId?: string;
  organizationId: string;
  [key: string]: unknown;
}

interface TaskAssignmentEventData {
  taskId: string;
  userId: string;
  organizationId: string;
  [key: string]: unknown;
}

/**
 * Simplified RealTimeSync - Focus on multi-user collaboration only
 * This component handles real-time events from OTHER users and updates the UI accordingly.
 * It doesn't handle complex deduplication or internal state management.
 */
// Debug: Track component instances to detect multiple mounts
if (typeof window !== 'undefined') {
  if (!window.__realtimeSyncInstances) {
    window.__realtimeSyncInstances = 0;
  }
}

type TaskListCache =
  | undefined
  | TaskWithAssignments[]
  | { tasks: TaskWithAssignments[]; totalCount?: number }
  | { data: TaskWithAssignments[]; totalCount?: number };

const RealTimeSync: FC = () => {
  // Debug: Count component instances
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__realtimeSyncInstances = (window.__realtimeSyncInstances || 0) + 1;
      
      if (import.meta.env.DEV && (window.__realtimeSyncInstances || 0) > 1) {
        logger.warn(`[RealTimeSync] ⚠️ Multiple RealTimeSync instances detected (${window.__realtimeSyncInstances}). This could cause event duplication!`);
      }
      
      return () => {
        window.__realtimeSyncInstances = Math.max(0, (window.__realtimeSyncInstances || 0) - 1);
      };
    }
  }, []);
  const { 
    threads, 
    addThreadComment, 
    removeThreadCommentById,
    updateThread,
    addThread,
    removeThread,
  } = useThreadStore();
  
  const queryClient = useQueryClient();
  const activeThreadsRef = useRef<Set<string>>(new Set());
  
  // Set up realtime core functionality
  const { 
    socket,
    isReady, 
    addEventListener, 
    broadcastToTabs,
    currentOrgId
  } = useRealtime();
  
  const taskListQueryKey = queryKeys.tasks.lists();
  const threadListQueryKey = queryKeys.threads.lists();

  const mutateTaskLists = (updater: (oldData: TaskListCache) => TaskListCache) => {
      queryClient.setQueriesData<TaskListCache>({ queryKey: taskListQueryKey }, (oldData) =>
        updater(oldData)
      );
    };

  const invalidateTaskDetail = (taskId: string) => queryClient.invalidateQueries({
        predicate: (query) => {
          const qk = query.queryKey;
          if (!Array.isArray(qk) || qk.length < 3) return false;
          const [scope, type, id] = qk as unknown[];
          return (
            scope === queryKeys.tasks.details()[0] &&
            type === queryKeys.tasks.details()[1] &&
            id === taskId
          );
        },
        refetchType: 'none',
      });

  // THREAD EVENTS SECTION
  
  // Listen for direct thread entity changes
  useEffect(() => {
    if (!isReady) return;
    
    // Listen for thread created events
    const cleanupThreadCreated = addEventListener(BroadcastEventType.THREAD_CREATED, (data: unknown) => {
      const threadData = data as ThreadEventData;
      if (!threadData || !threadData.id) return;
      
      // Directly add the thread to the store
      addThread(threadData);
    });
    
    // Listen for thread updated events
    const cleanupThreadUpdated = addEventListener(BroadcastEventType.THREAD_UPDATED, (data: unknown) => {
      const threadData = data as ThreadEventData;
      if (!threadData || !threadData.id) return;
      
      // Directly update the thread in the store
      updateThread(threadData);
    });
    
    // Listen for thread deleted events
    const cleanupThreadDeleted = addEventListener(BroadcastEventType.THREAD_DELETED, (data: unknown) => {
      const threadData = data as ThreadEventData;
      if (!threadData || !threadData.id) return;
      
      // Remove the thread from the store
      removeThread(threadData.id);
    });
    
    return () => {
      cleanupThreadCreated();
      cleanupThreadUpdated();
      cleanupThreadDeleted();
    };
  }, [isReady, addEventListener, addThread, updateThread, removeThread]);
  
  // Listen for thread comment events
  useEffect(() => {
    if (!isReady) return;
    
    // Listen for thread comment created events
    const cleanupCommentCreated = addEventListener(BroadcastEventType.THREAD_COMMENT_CREATED, (data: unknown) => {
      const commentData = data as ThreadComment;
      if (!commentData?.id || !commentData?.threadId) return;
      
      // Add the comment directly to the store instead of refetching all threads
      addThreadComment(commentData);
    });
    
    // Listen for thread comment deleted events
    const cleanupCommentDeleted = addEventListener(BroadcastEventType.THREAD_COMMENT_DELETED, (data: unknown) => {
      const commentData = data as ThreadCommentEventData;
      if (!commentData || !commentData.id || !commentData.threadId) return;
      
      // Remove the comment directly from the store
      removeThreadCommentById(commentData.id);
    });
    
    return () => {
      cleanupCommentCreated();
      cleanupCommentDeleted();
    };
  }, [isReady, addEventListener, addThreadComment, removeThreadCommentById]);
  
  // TASK EVENTS SECTION - Simplified for multi-user collaboration
  
  // Listen for task events from the socket directly.
  // Task sync previously depended on another mounted hook bridging raw socket
  // events into the internal realtime bus. That made updates disappear on pages
  // that did not mount a task-specific realtime hook.
  useEffect(() => {
    if (!isReady || !socket) return;
    
    // Listen for task created events
    const handleTaskCreated = (data: unknown) => {
      const taskData = data as TaskWithAssignments;
      if (!taskData?.id) return;
      
      // Simple invalidation - let React Query handle the rest
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.tasks.all,
        refetchType: 'active' 
      });
    };
    
    // Listen for task updated events
    const handleTaskUpdated = (data: unknown) => {
      const taskData = data as TaskWithAssignments;
      if (!taskData?.id) return;
      
      // Debug: Count events to detect cascade issues
      if (import.meta.env.DEV) {
        if (!window.__realtimeEventCounter) {
          window.__realtimeEventCounter = new Map();
        }
        const eventKey = `task-updated-${taskData.id}`;
        const count = ((window.__realtimeEventCounter.get(eventKey) || 0) + 1);
        window.__realtimeEventCounter.set(eventKey, count);
        
        // Only log first few events and then warn about excessive events
        if (count <= 3) {
          /* Normal event count - no logging needed */
        } else if (count === 10) {
          logger.warn(`[RealTimeSync] ⚠️ Excessive events detected for task ${taskData.id} (${count} events) - possible cascade effect!`);
        }
        
        // Reset counter after 5 seconds of no events
        setTimeout(() => {
          window.__realtimeEventCounter?.delete(eventKey);
        }, 5000);
      }
      
      // Initialize event tracker if needed
      if (!window.__realtimeEventTracker) {
        window.__realtimeEventTracker = new Map();
      }
      
      // Track this event to prevent duplicate processing
      const recentlyProcessedKey = `task-update-${taskData.id}`;
      window.__realtimeEventTracker.set(recentlyProcessedKey, Date.now());
      
      // Use surgical updates to prevent global avatar flashing cascade
      // Only invalidate specific task queries, not all tasks globally
      invalidateTaskDetail(taskData.id);
      
      // Also surgically update tasks queries to avoid full refetch
      mutateTaskLists((oldData) => {
        if (!oldData) return oldData;
        if (Array.isArray(oldData)) {
          return oldData.map((task) => (task.id === taskData.id ? { ...task, ...taskData } : task));
        }
        if ('tasks' in oldData && Array.isArray(oldData.tasks)) {
          return {
            ...oldData,
            tasks: oldData.tasks.map((task) => (task.id === taskData.id ? { ...task, ...taskData } : task)),
          };
        }
        if ('data' in oldData && Array.isArray(oldData.data)) {
          return {
            ...oldData,
            data: oldData.data.map((task) => (task.id === taskData.id ? { ...task, ...taskData } : task)),
          };
        }
        return oldData;
      });
    };
    
    // Listen for task deleted events
    const handleTaskDeleted = (data: unknown) => {
      const deleteData = data as TaskDeletedEventData;
      const taskId = deleteData?.id || deleteData?.taskId;
      if (!taskId) return;
      
      // Reduced logging to prevent console spam
      if (import.meta.env.DEV && Math.random() < 0.2) {
        /* Debug logging placeholder - add task deletion logging here if needed */
      }
      
      // Use surgical updates to prevent global avatar flashing
      // Remove the specific task from cache instead of invalidating all tasks
      mutateTaskLists((oldData) => {
        if (!oldData) return oldData;
        const adjustCount = (totalCount?: number) => Math.max(0, (totalCount || 0) - 1);

        if (Array.isArray(oldData)) {
          return oldData.filter((task) => task.id !== taskId);
        }

        if ('tasks' in oldData && Array.isArray(oldData.tasks)) {
          return {
            ...oldData,
            tasks: oldData.tasks.filter((task) => task.id !== taskId),
            totalCount: adjustCount(oldData.totalCount),
          };
        }

        if ('data' in oldData && Array.isArray(oldData.data)) {
          return {
            ...oldData,
            data: oldData.data.filter((task) => task.id !== taskId),
            totalCount: adjustCount(oldData.totalCount),
          };
        }

        return oldData;
      });
    };
    
    // Listen for task status updated events
    const handleTaskStatusUpdated = (data: unknown) => {
      const statusData = data as { taskId: string; statusId: string; organizationId: string };
      if (!statusData?.taskId || !statusData?.statusId) return;
      
      // Reduced logging to prevent console spam
      if (import.meta.env.DEV && Math.random() < 0.2) {
        /* Debug logging placeholder - add status update logging here if needed */
      }

      // Use surgical updates to prevent global avatar flashing
      // Only invalidate specific task, don't refetch all tasks
      invalidateTaskDetail(statusData.taskId);
    };
    
    // Listen for task assignee events
    const handleTaskAssigneeAdded = (data: unknown) => {
      const assignmentData = data as TaskAssignmentEventData;
      if (!assignmentData?.taskId || !assignmentData?.userId) return;

      // Reduced logging to prevent console spam
      if (import.meta.env.DEV && Math.random() < 0.1) {
        /* Debug logging placeholder - add assignee added logging here if needed */
      }

      // Initialize event tracker if needed
      if (!window.__realtimeEventTracker) {
        window.__realtimeEventTracker = new Map();
      }
      
      // Skip assignee events if we already processed a task:updated event
      // This prevents duplicate processing since backend sends both events
      const recentlyProcessedKey = `task-update-${assignmentData.taskId}`;
      const now = Date.now();
      if (window.__realtimeEventTracker?.has(recentlyProcessedKey)) {
        const lastProcessed = window.__realtimeEventTracker.get(recentlyProcessedKey) || 0;
        if (now - lastProcessed < 1000) { // Skip if processed within 1 second
          // Skip duplicate event silently - no need to log every skip
          return;
        }
      }
      
      // Use surgical updates to prevent global avatar flashing
      // Only invalidate specific task, don't refetch all tasks
      invalidateTaskDetail(assignmentData.taskId);
    };
    
    const handleTaskAssigneeRemoved = (data: unknown) => {
      const assignmentData = data as TaskAssignmentEventData;
      if (!assignmentData?.taskId || !assignmentData?.userId) return;

      // Reduced logging to prevent console spam
      if (import.meta.env.DEV && Math.random() < 0.1) {
        /* Debug logging placeholder - add assignee removed logging here if needed */
      }

      // Initialize event tracker if needed
      if (!window.__realtimeEventTracker) {
        window.__realtimeEventTracker = new Map();
      }
      
      // Skip assignee events if we already processed a task:updated event
      // This prevents duplicate processing since backend sends both events
      const recentlyProcessedKey = `task-update-${assignmentData.taskId}`;
      const now = Date.now();
      if (window.__realtimeEventTracker?.has(recentlyProcessedKey)) {
        const lastProcessed = window.__realtimeEventTracker.get(recentlyProcessedKey) || 0;
        if (now - lastProcessed < 1000) { // Skip if processed within 1 second
          // Skip duplicate event silently - no need to log every skip
          return;
        }
      }
      
      // Use surgical updates to prevent global avatar flashing
      // Only invalidate specific task, don't refetch all tasks
      invalidateTaskDetail(assignmentData.taskId);
    };

    // Listen for task comment count updates (lightweight event)
    const handleTaskCommentCountUpdated = (data: unknown) => {
      const payload = data as { taskId: string; commentCount: number };
      if (!payload?.taskId) return;

      // Surgically update only the commentCount field in all task caches
      // This is just updating a number - it shouldn't cause popover issues
      // We use structural sharing so React Query only re-renders if the count actually changed
      mutateTaskLists((oldData) => {
        if (!oldData) return oldData;

        if (Array.isArray(oldData)) {
          const updated = oldData.map((task) =>
            task.id === payload.taskId && task.commentCount !== payload.commentCount
              ? { ...task, commentCount: payload.commentCount }
              : task
          );
          // Return same reference if nothing changed
          return updated.some((task, i) => task !== oldData[i]) ? updated : oldData;
        }

        if ('tasks' in oldData && Array.isArray(oldData.tasks)) {
          const updatedTasks = oldData.tasks.map((task) =>
            task.id === payload.taskId && task.commentCount !== payload.commentCount
              ? { ...task, commentCount: payload.commentCount }
              : task
          );
          // Return same reference if nothing changed
          return updatedTasks.some((task, i) => task !== oldData.tasks[i])
            ? { ...oldData, tasks: updatedTasks }
            : oldData;
        }

        if ('data' in oldData && Array.isArray(oldData.data)) {
          const updatedData = oldData.data.map((task) =>
            task.id === payload.taskId && task.commentCount !== payload.commentCount
              ? { ...task, commentCount: payload.commentCount }
              : task
          );
          // Return same reference if nothing changed
          return updatedData.some((task, i) => task !== oldData.data[i])
            ? { ...oldData, data: updatedData }
            : oldData;
        }

        return oldData;
      });
    };

    socket.on(SocketEvent.TASK_CREATED, handleTaskCreated);
    socket.on(SocketEvent.TASK_UPDATED, handleTaskUpdated);
    socket.on(SocketEvent.TASK_DELETED, handleTaskDeleted);
    socket.on(SocketEvent.TASK_STATUS_UPDATED, handleTaskStatusUpdated);
    socket.on(SocketEvent.TASK_ASSIGNEE_ADDED, handleTaskAssigneeAdded);
    socket.on(SocketEvent.TASK_ASSIGNEE_REMOVED, handleTaskAssigneeRemoved);
    socket.on(
      SocketEvent.TASK_COMMENT_COUNT_UPDATED,
      handleTaskCommentCountUpdated,
    );

    return () => {
      socket.off(SocketEvent.TASK_CREATED, handleTaskCreated);
      socket.off(SocketEvent.TASK_UPDATED, handleTaskUpdated);
      socket.off(SocketEvent.TASK_DELETED, handleTaskDeleted);
      socket.off(SocketEvent.TASK_STATUS_UPDATED, handleTaskStatusUpdated);
      socket.off(SocketEvent.TASK_ASSIGNEE_ADDED, handleTaskAssigneeAdded);
      socket.off(SocketEvent.TASK_ASSIGNEE_REMOVED, handleTaskAssigneeRemoved);
      socket.off(
        SocketEvent.TASK_COMMENT_COUNT_UPDATED,
        handleTaskCommentCountUpdated,
      );
    };
  }, [isReady, socket, queryClient, mutateTaskLists, invalidateTaskDetail]);
  
  // ACTIVE THREAD TRACKING SECTION
  
  // Track which threads are active in this tab
  useEffect(() => {
    if (!isReady || !currentOrgId) return;
    
    // Update active threads reference
    activeThreadsRef.current = new Set(threads.map(thread => thread.id));
    
    // Send heartbeat only if we have threads
    if (threads.length > 0) {
      // Broadcast active status to other tabs for each thread
      threads.forEach(thread => {
        const message: BroadcastMessage = {
          type: BroadcastEventType.ACTIVE_THREADS_HEARTBEAT,
          entityType: 'thread',
          entityId: thread.id,
          data: { active: true, threadId: thread.id, timestamp: Date.now() },
          timestamp: Date.now(),
          messageId: `heartbeat-${thread.id}-${Date.now()}`,
          source: 'client',
          tabId: 'current'
        };
        broadcastToTabs(message);
      });
    }
    
    // Set up regular heartbeat to maintain active thread awareness
    const heartbeatInterval = setInterval(() => {
      // Only send heartbeat if we have threads and are connected
      if (isReady && threads.length > 0) {
        // Periodically re-broadcast active threads to maintain awareness
        threads.forEach(thread => {
          const message: BroadcastMessage = {
            type: BroadcastEventType.ACTIVE_THREADS_HEARTBEAT,
            entityType: 'thread',
            entityId: thread.id,
            data: { active: true, threadId: thread.id, timestamp: Date.now() },
            timestamp: Date.now(),
            messageId: `heartbeat-${thread.id}-${Date.now()}`,
            source: 'client',
            tabId: 'current'
          };
          broadcastToTabs(message);
        });
      }
    }, 30000); // Send heartbeat every 30 seconds
    
    return () => {
      clearInterval(heartbeatInterval);
    };
  }, [isReady, currentOrgId, threads, broadcastToTabs]);
  
  // Listen for active threads heartbeat to track which threads are open in other tabs
  useEffect(() => {
    if (!isReady) return;
    
    const cleanupHeartbeat = addEventListener(BroadcastEventType.ACTIVE_THREADS_HEARTBEAT, (data: unknown) => {
      const heartbeatData = data as { threadId: string };
      if (!heartbeatData || !heartbeatData.threadId) return;
      
      // Handle active thread heartbeats here for potential future use
    });
    
    return () => {
      cleanupHeartbeat();
    };
  }, [isReady, addEventListener]);
  
  // FALLBACK REFETCH MECHANISM
  // This ensures that any complex event that can't be handled with direct store updates
  // will still result in the thread list being refreshed
  useEffect(() => {
    if (!isReady) return;
    
    // Define complex events that might require a full refetch
    const complexEvents: BroadcastEventType[] = [
      // Add any other complex events that might require a full refetch
    ];
    
    const cleanupFunctions: (() => void)[] = [];
    
    complexEvents.forEach(eventType => {
      const cleanup = addEventListener(eventType, () => {
        // Refresh thread data as a last resort
        // Invalidate thread queries instead of fetching directly
        queryClient.invalidateQueries({ 
          queryKey: threadListQueryKey,
          refetchType: 'active' // Only refetch active queries
        });
      });
      
      cleanupFunctions.push(cleanup);
    });
    
    return () => {
      cleanupFunctions.forEach(cleanup => cleanup());
    };
  }, [isReady, addEventListener, queryClient, threadListQueryKey]);
  
  // This component doesn't render anything
  return null;
};

export default RealTimeSync; 
